'use strict'

import {
  cacheReadIndex,
  resolveGeoTiffActiveRestoreCandidate,
  buildGeoTiffDescriptor,
  buildGeoTiffFileFromDescriptor,
  readGeoTiffActiveDescriptor,
  writeGeoTiffActiveDescriptor
} from './portal-geotiff-cache.js'

  const bindCoreUiHandlers = function ({
    _,
    $,
    $body,
    $leafletmap,
    map,
    db,
    store,
    Swal,
    areaSwal,
    fnClampGeoTiffResolution,
    fnGetGeoTiffResolution,
    fnT,
    fnTF,
    fnLoadGeoTiffFile,
    fnSetMyLocationButtonActive,
    fnClearMyLocationLocateTimer,
    fnTryBrowserMyLocationFallback,
    fnLayerFilterAll,
    fnGetLayers,
    fnClearGeoTiffLayer,
    fnGetTiffLastFile,
    fnSetTiffLastFile,
    fnGetMyLocationActive,
    fnSetMyLocationFallbackTried,
    fnGetMyLocationMarker,
    fnSetMyLocationMarker,
    fnSetMyLocationLocateTimer
  }) {
    let geotiffAutoRestoreStarted = false
    let route3dGeoTiffSyncTimer = null
    let route3dGeoTiffSyncSequence = 0

    const fnQueueRoute3dSyncAfterGeoTiffLoad = function () {
      const syncDelaysMs = [0, 250, 1200, 3000]
      route3dGeoTiffSyncSequence += 1
      const currentSequence = route3dGeoTiffSyncSequence
      if (route3dGeoTiffSyncTimer) {
        clearTimeout(route3dGeoTiffSyncTimer)
        route3dGeoTiffSyncTimer = null
      }
      const fnRunAttempt = function (attemptIndex) {
        if (currentSequence !== route3dGeoTiffSyncSequence) return
        const fnScheduleRoute3dSharedSync = (typeof window !== 'undefined' && typeof window.PORTAL_SCHEDULE_ROUTE3D_SHARED_SYNC === 'function')
          ? window.PORTAL_SCHEDULE_ROUTE3D_SHARED_SYNC
          : null
        if (fnScheduleRoute3dSharedSync) {
          fnScheduleRoute3dSharedSync()
        }
        const nextAttemptIndex = attemptIndex + 1
        if (nextAttemptIndex >= syncDelaysMs.length) {
          route3dGeoTiffSyncTimer = null
          return
        }
        route3dGeoTiffSyncTimer = setTimeout(function () {
          fnRunAttempt(nextAttemptIndex)
        }, syncDelaysMs[nextAttemptIndex])
      }
      fnRunAttempt(0)
    }

    const GEO_TIFF_SOURCE_TYPES = ['terrain', 'ortho']
    const GEO_TIFF_RECENT_MAX = 8
    const GEO_TIFF_RECENT_STORAGE_KEY_PREFIX = 'portal-geotiff-recent-v1:'
    const fnIsElectronRuntime = function () {
      return !!(typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron === true)
    }
    console.log('[Electron][Bridge] runtime check', {
      isElectronRuntime: fnIsElectronRuntime(),
      hasPicker: !!(typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.pickGeoTiffFile === 'function')
    })
    const fnNormalizeSourceType = function (rawSourceType) {
      const sourceType = String(rawSourceType || '').trim().toLowerCase()
      return sourceType === 'ortho' ? 'ortho' : 'terrain'
    }
    const fnGetSourceCacheKeyPrefix = function (rawSourceType) {
      return 'portal-geotiff-cache-file-v1:' + fnNormalizeSourceType(rawSourceType) + ':'
    }
    const fnGetSourceCacheIndexKey = function (rawSourceType) {
      return 'portal-geotiff-cache-index-v1:' + fnNormalizeSourceType(rawSourceType)
    }
    const fnEnsureGeoTiffRecentStore = function () {
      if (!db.settings || typeof db.settings !== 'object') db.settings = {}
      if (!db.settings.tiffRecent || typeof db.settings.tiffRecent !== 'object') {
        db.settings.tiffRecent = { terrain: [], ortho: [] }
      }
      if (!Array.isArray(db.settings.tiffRecent.terrain)) db.settings.tiffRecent.terrain = []
      if (!Array.isArray(db.settings.tiffRecent.ortho)) db.settings.tiffRecent.ortho = []
      GEO_TIFF_SOURCE_TYPES.forEach(function (sourceType) {
        if (db.settings.tiffRecent[sourceType].length) return
        const hydrated = fnReadRecentListFromLocalStorage(sourceType)
        if (hydrated.length) {
          db.settings.tiffRecent[sourceType] = hydrated
        }
      })
      return db.settings.tiffRecent
    }
    const fnReadRecentListFromLocalStorage = function (rawSourceType) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      try {
        const storage = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null
        if (!storage) return []
        const raw = String(storage.getItem(GEO_TIFF_RECENT_STORAGE_KEY_PREFIX + sourceType) || '').trim()
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.map(fnNormalizeRecentEntry).filter(Boolean).slice(0, GEO_TIFF_RECENT_MAX)
      } catch (_error) {
        return []
      }
    }
    const fnWriteRecentListToLocalStorage = function (rawSourceType, nextList) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      try {
        const storage = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null
        if (!storage) return
        const normalized = (Array.isArray(nextList) ? nextList : []).map(fnNormalizeRecentEntry).filter(Boolean).slice(0, GEO_TIFF_RECENT_MAX)
        storage.setItem(GEO_TIFF_RECENT_STORAGE_KEY_PREFIX + sourceType, JSON.stringify(normalized))
      } catch (_error) {}
    }
    const fnGetRecentList = function (rawSourceType) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      const storeRef = fnEnsureGeoTiffRecentStore()
      return Array.isArray(storeRef[sourceType]) ? storeRef[sourceType] : []
    }
    const fnSetRecentList = function (rawSourceType, nextList) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      const storeRef = fnEnsureGeoTiffRecentStore()
      storeRef[sourceType] = Array.isArray(nextList) ? nextList.slice(0, GEO_TIFF_RECENT_MAX) : []
      fnWriteRecentListToLocalStorage(sourceType, storeRef[sourceType])
      if (store && typeof store.setAll === 'function') {
        void store.setAll(db)
      }
    }
    const fnTruncateRecentPath = function (rawPath) {
      const text = String(rawPath || '').trim()
      if (!text) return ''
      const maxLen = 100
      if (text.length <= maxLen) return text
      return '...' + text.slice(-maxLen)
    }
    const fnNormalizeRecentEntry = function (entry) {
      if (!entry || typeof entry !== 'object') return null
      const signature = String(entry.signature || '').trim()
      const name = String(entry.name || '').trim()
      const sourcePath = String(entry.sourcePath || entry.path || '').trim()
      const size = Number(entry.size || 0)
      const lastModified = Number(entry.lastModified || 0)
      const type = String(entry.type || 'image/tiff').trim() || 'image/tiff'
      if (!signature || !name || !(size > 0)) return null
      return {
        signature: signature,
        name: name,
        sourcePath: sourcePath,
        size: size,
        lastModified: Number.isFinite(lastModified) && lastModified > 0 ? lastModified : 0,
        type: type
      }
    }
    const fnPushRecentEntry = function (rawSourceType, descriptor) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      const normalized = fnNormalizeRecentEntry(descriptor)
      console.log('[GeoTIFF][Recent] fnPushRecentEntry', { sourceType, descriptor, normalized })
      if (!normalized) {
        console.warn('[GeoTIFF][Recent] fnNormalizeRecentEntry returned null — entry NOT pushed')
        return
      }
      const list = fnGetRecentList(sourceType)
      const nextList = [normalized].concat(list.filter(function (row) {
        return row && String(row.signature || '').trim() !== normalized.signature
      })).slice(0, GEO_TIFF_RECENT_MAX)
      fnSetRecentList(sourceType, nextList)
      fnRenderRecentMenuList(sourceType)
    }
    const fnBuildRecentButtonLabel = function (entry) {
      const sourcePath = String(entry && entry.sourcePath || '').trim()
      const name = String(entry && entry.name || '').trim()
      const preferred = sourcePath || name || 'ismeretlen.tif'
      return fnTruncateRecentPath(preferred)
    }
    const fnEscapeHtmlLite = function (rawValue) {
      return String(rawValue || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }
    const fnRenderRecentMenuList = function (rawSourceType) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      const selector = sourceType === 'terrain'
        ? '[data-tiff-recent-terrain-list]'
        : '[data-tiff-recent-ortho-list]'
      const rootSelector = sourceType === 'terrain'
        ? '[data-tiff-recent-terrain-root]'
        : '[data-tiff-recent-ortho-root]'
      const $host = $body.find(selector)
      const $root = $body.find(rootSelector)
      if (!$host.length || !$root.length) {
        return
      }
      const $template = $host.find('[data-tiff-recent-menu-item-template]').first()
      const $empty = $host.find('[data-tiff-recent-empty-placeholder]').first()
      if (!$template.length) {
        console.warn('[GeoTIFF][Recent] fnRenderRecentMenuList: template button not found in $host', { sourceType, selector })
        return
      }
      $host.find('[data-tiff-recent-menu-item-rendered]').remove()
      const list = fnGetRecentList(sourceType)
      console.log('[GeoTIFF][Recent] fnRenderRecentMenuList', { sourceType, listLen: list.length, hostLen: $host.length, rootLen: $root.length })
      if (!list.length) {
        if ($empty.length) $empty.removeClass('d-none')
        $root.removeClass('d-none')
        return
      }
      if ($empty.length) $empty.addClass('d-none')
      list.forEach(function (entry, idx) {
        const label = fnBuildRecentButtonLabel(entry)
        const $row = $template.clone()
        $row.removeClass('d-none')
        $row.attr('data-tiff-recent-menu-item', '1')
        $row.attr('data-tiff-recent-menu-item-rendered', '1')
        $row.attr('data-tiff-source-type', sourceType)
        $row.attr('data-tiff-recent-index', String(idx))
        $row.attr('title', label)
        $row.text(label)
        $host.append($row)
      })
      $root.removeClass('d-none')
    }

    const fnClearLocalTerrainStateAfterUnload = function (rawSourceType) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      if (sourceType !== 'terrain' || typeof window === 'undefined') return
      window.PORTAL_LOCAL_TERRAIN_STATE = null
      if (
        db &&
        db.settings &&
        db.settings.mapCursorElevation &&
        db.settings.mapCursorElevation.provider === 'localDEM'
      ) {
        db.settings.mapCursorElevation.provider = 'gDEM'
      }
    }

    const fnPersistExplicitTiffUnload = async function (rawSourceType) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      const idbKeyval = await fnGetIdbKeyvalWithRetry()
      if (!idbKeyval) return false
      return await writeGeoTiffActiveDescriptor({ unloaded: true }, {
        idbKeyval: idbKeyval,
        fnT: fnT,
        GEOTIFF_CACHE_INDEX_KEY: fnGetSourceCacheIndexKey(sourceType),
        GEOTIFF_CACHE_KEY_PREFIX: fnGetSourceCacheKeyPrefix(sourceType)
      })
    }

    const fnBuildFileFromElectronPick = function (picked) {
      if (!picked || picked.canceled) return null
      const pickedPath = String(picked.path || '').trim()
      const pickedName = String(picked.name || '').trim() || 'ismeretlen.tif'
      const lastModified = Number(picked.lastModified || Date.now())
      if (!pickedPath) return null
      const file = {
        name: pickedName,
        type: String(picked.type || 'image/tiff').trim() || 'image/tiff',
        size: Number(picked.size || 0),
        lastModified: Number.isFinite(lastModified) ? lastModified : Date.now(),
        sourcePath: pickedPath,
        path: pickedPath
      }
      return {
        file: file,
        sourcePath: pickedPath
      }
    }
    const fnSelectFileViaElectronPicker = async function () {
      if (!fnIsElectronRuntime()) return null
      if (!window.electronAPI || typeof window.electronAPI.pickGeoTiffFile !== 'function') return null
      const picked = await window.electronAPI.pickGeoTiffFile()
      return fnBuildFileFromElectronPick(picked)
    }

    const fnGetIdbKeyvalWithRetry = async function () {
      const maxAttempts = 10
      const waitMs = 150
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const idbKeyval = (typeof window !== 'undefined' && window.idbKeyval && typeof window.idbKeyval.get === 'function')
          ? window.idbKeyval
          : null
        if (idbKeyval) return idbKeyval
        await new Promise(function (resolve) { setTimeout(resolve, waitMs) })
      }
      return null
    }
    const fnEnsureActiveDescriptorForSource = async function (rawSourceType, rawDescriptorCandidate) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      const idbKeyval = await fnGetIdbKeyvalWithRetry()
      const sourceCacheIndexKey = fnGetSourceCacheIndexKey(sourceType)
      const sourceCacheKeyPrefix = fnGetSourceCacheKeyPrefix(sourceType)
      let fallbackDescriptor = fnNormalizeRecentEntry(rawDescriptorCandidate) || null
      if (!idbKeyval) return fallbackDescriptor || buildGeoTiffDescriptor(rawDescriptorCandidate) || null
      let currentDescriptor = null
      let cacheIndex = null
      try {
        cacheIndex = await cacheReadIndex({
          idbKeyval: idbKeyval,
          fnT: fnT,
          GEOTIFF_CACHE_INDEX_KEY: sourceCacheIndexKey,
          GEOTIFF_CACHE_KEY_PREFIX: sourceCacheKeyPrefix
        })
        currentDescriptor = await readGeoTiffActiveDescriptor({
          idbKeyval: idbKeyval,
          fnT: fnT,
          GEOTIFF_CACHE_INDEX_KEY: sourceCacheIndexKey,
          GEOTIFF_CACHE_KEY_PREFIX: sourceCacheKeyPrefix
        })
      } catch (_error) {}
      if (!fallbackDescriptor) {
        const entries = (cacheIndex && cacheIndex.entries && typeof cacheIndex.entries === 'object') ? cacheIndex.entries : {}
        const candidateName = String(rawDescriptorCandidate && rawDescriptorCandidate.name || '').trim()
        const candidateSize = Number(rawDescriptorCandidate && rawDescriptorCandidate.size || 0)
        const candidateLastModified = Number(rawDescriptorCandidate && rawDescriptorCandidate.lastModified || 0)
        const matchedSignature = Object.keys(entries).find(function (signature) {
          const entry = entries[signature]
          if (!entry) return false
          if (candidateName && String(entry.name || '').trim() !== candidateName) return false
          if (candidateSize > 0 && Number(entry.size || 0) !== candidateSize) return false
          if (candidateLastModified > 0 && Number(entry.lastModified || 0) !== candidateLastModified) return false
          return true
        })
        if (matchedSignature) {
          fallbackDescriptor = buildGeoTiffDescriptor(entries[matchedSignature], matchedSignature)
        }
      }
      if (!fallbackDescriptor) {
        const rawFileDescriptor = buildGeoTiffDescriptor(rawDescriptorCandidate) || null
        if (rawFileDescriptor) {
          fallbackDescriptor = rawFileDescriptor
        }
      }
      if (currentDescriptor && currentDescriptor.unloaded !== true) {
        return currentDescriptor
      }
      if (!fallbackDescriptor) {
        return currentDescriptor
      }
      const rewritten = await writeGeoTiffActiveDescriptor(fallbackDescriptor, {
        idbKeyval: idbKeyval,
        fnT: fnT,
        GEOTIFF_CACHE_INDEX_KEY: sourceCacheIndexKey,
        GEOTIFF_CACHE_KEY_PREFIX: sourceCacheKeyPrefix
      })
      console.warn('[GeoTIFF][Restore] active descriptor repaired after stale unload marker', {
        sourceType: sourceType,
        rewritten: rewritten,
        fallbackDescriptor: fallbackDescriptor
      })
      if (!rewritten) return fallbackDescriptor
      try {
        currentDescriptor = await readGeoTiffActiveDescriptor({
          idbKeyval: idbKeyval,
          fnT: fnT,
          GEOTIFF_CACHE_INDEX_KEY: sourceCacheIndexKey,
          GEOTIFF_CACHE_KEY_PREFIX: sourceCacheKeyPrefix
        })
      } catch (_error) {
        currentDescriptor = null
      }
      if (currentDescriptor && currentDescriptor.unloaded !== true) {
        return currentDescriptor
      }
      return fallbackDescriptor
    }

    const fnAutoRestoreGeoTiffFromCache = async function () {
      if (geotiffAutoRestoreStarted) return
      geotiffAutoRestoreStarted = true
      try {
        const idbKeyval = await fnGetIdbKeyvalWithRetry()
        if (!idbKeyval) {
          console.warn('[GeoTIFF][AutoRestore] idbKeyval unavailable, auto-restore skipped')
          return
        }
        for (const sourceType of GEO_TIFF_SOURCE_TYPES) {
          const allowDescriptorOnlyRestore = fnIsElectronRuntime() === true
          const candidate = await resolveGeoTiffActiveRestoreCandidate({
            idbKeyval: idbKeyval,
            fnT: fnT,
            allowDescriptorOnly: allowDescriptorOnlyRestore,
            GEOTIFF_CACHE_INDEX_KEY: fnGetSourceCacheIndexKey(sourceType),
            GEOTIFF_CACHE_KEY_PREFIX: fnGetSourceCacheKeyPrefix(sourceType)
          })
          if (!candidate || !candidate.file) continue
          const resolutionSettingKey = sourceType === 'terrain' ? 'tiffResolutionTerrain' : 'tiffResolutionOrtho'
          const resolutionInSettings = (db && db.settings && db.settings[resolutionSettingKey])
            ? db.settings[resolutionSettingKey]
            : fnGetGeoTiffResolution(candidate.file.size)
          const resolution = fnClampGeoTiffResolution(resolutionInSettings)
          const autoAlignTerrain = !(db && db.settings && db.settings.tiffAutoAlignTerrain === false)
          fnSetTiffLastFile(candidate.file, sourceType)
          const restored = await fnLoadGeoTiffFile(candidate.file, {
            sourceType: sourceType,
            resolution: resolution,
            mode: sourceType,
            autoAlignTerrain: autoAlignTerrain,
            arrayBuffer: candidate.arrayBuffer
          })
          if (!restored) {
            console.warn('[GeoTIFF][AutoRestore] skipped', {
              sourceType: sourceType,
              reason: 'load_returned_false'
            })
            continue
          }
          console.info('[GeoTIFF][AutoRestore] restored', {
            sourceType: sourceType,
            signature: candidate.signature,
            descriptorOnly: candidate.descriptorOnly === true
          })
          fnPushRecentEntry(sourceType, candidate.entry || candidate.descriptor || candidate.file)
          fnQueueRoute3dSyncAfterGeoTiffLoad()
        }
      } catch (error) {
        console.error('[GeoTIFF][AutoRestore] failed', {
          error: String((error && error.message) || error || '')
        })
      }
    }

    const fnOpenGeoTiffImportModal = function (rawSourceType) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      const resolutionSettingKey = sourceType === 'terrain' ? 'tiffResolutionTerrain' : 'tiffResolutionOrtho'
      const titleKey = sourceType === 'terrain' ? 'geotiff.import_dem_title' : 'geotiff.import_ortho_title'
      const defaultResolution = fnClampGeoTiffResolution((db.settings && db.settings[resolutionSettingKey]) ? db.settings[resolutionSettingKey] : fnGetGeoTiffResolution(0))
      const tiffPopupTpl = String($('[data-tpl-geotiff-import-popup]').html() || '').trim()
      if (!tiffPopupTpl) {
        Swal.fire(fnT('geotiff.template_missing'))
        return
      }
      const tiffPopupHtml = tiffPopupTpl.replace(/__DEFAULT_RESOLUTION__/g, String(defaultResolution))
      let selectedFile = fnGetTiffLastFile(sourceType)
      let selectedRecentDescriptor = null
      let autoRecalcInFlight = false
      let autoRecalcQueued = false
      const fnGetAutoAlignFromSettings = function () {
        if (!db.settings || typeof db.settings !== 'object') db.settings = {}
        if (db.settings.tiffAutoAlignTerrain === undefined || db.settings.tiffAutoAlignTerrain === null) return true
        return db.settings.tiffAutoAlignTerrain !== false
      }
      const recentList = fnGetRecentList(sourceType)
      areaSwal.fire({
        width: 600,
        title: fnT(titleKey),
        showCancelButton: true,
        confirmButtonText: fnT('geotiff.import_ok'),
        cancelButtonText: fnT('common.cancel'),
        html: tiffPopupHtml,
        preConfirm: function () {
          if (!selectedFile) {
            Swal.showValidationMessage(fnT('geotiff.import_choose_file'))
            return false
          }
          const $popup = $(Swal.getPopup())
          const currentResolution = fnClampGeoTiffResolution($popup.find('[data-tiff-resolution-number]').val())
          const autoAlignTerrain = $popup.find('[data-tiff-auto-align]').is(':checked')
          return {
            file: selectedFile,
            descriptor: selectedRecentDescriptor,
            sourceType: sourceType,
            resolution: currentResolution,
            mode: sourceType,
            autoAlignTerrain: autoAlignTerrain
          }
        },
        didOpen: function (modal) {
          const $modal = $(modal)
          const $file = $modal.find('[data-file-input]')
          const isElectronRuntime = fnIsElectronRuntime()
          const $selectedFile = $modal.find('[data-tiff-selected-file]')
          const $range = $modal.find('[data-tiff-resolution-range]')
          const $number = $modal.find('[data-tiff-resolution-number]')
          const $recentWrap = $modal.find('[data-tiff-recent-wrap]')
          const $recentSelect = $modal.find('[data-tiff-recent-select]')
          const $autoAlign = $modal.find('[data-tiff-auto-align]')
          const $autoAlignWrap = $modal.find('[data-tiff-auto-align-wrap]')
          const $autoAlignHelp = $modal.find('[data-tiff-auto-align-help]')
          const fnPersistAutoAlign = function (nextValue) {
            if (!db.settings || typeof db.settings !== 'object') db.settings = {}
            db.settings.tiffAutoAlignTerrain = (nextValue !== false)
            if (store && typeof store.setAll === 'function') {
              void store.setAll(db)
            }
          }
          const fnSetResolutionInputs = function (value) {
            const normalized = fnClampGeoTiffResolution(value)
            $range.val(normalized)
            $number.val(normalized)
            return normalized
          }
          let selectedResolution = fnSetResolutionInputs(defaultResolution)
          $autoAlign.prop('checked', fnGetAutoAlignFromSettings())
          if (sourceType === 'ortho') {
            $autoAlignWrap.addClass('d-none')
            $autoAlignHelp.addClass('d-none')
            $autoAlign.prop('checked', false)
          }
          if (!recentList.length) {
            $recentWrap.addClass('d-none')
          } else if (!fnIsElectronRuntime()) {
            $recentWrap.addClass('d-none')
          } else {
            const options = ['<option value="">' + fnT('geotiff.popup.recent_empty') + '</option>']
            recentList.forEach(function (entry, idx) {
              options.push('<option value="' + String(idx) + '">' + fnEscapeHtmlLite(fnBuildRecentButtonLabel(entry)) + '</option>')
            })
            $recentSelect.html(options.join(''))
            $recentSelect.on('change', function () {
              const selectedIndex = parseInt(String($recentSelect.val() || ''), 10)
              if (!Number.isFinite(selectedIndex) || selectedIndex < 0 || selectedIndex >= recentList.length) return
              const descriptor = fnNormalizeRecentEntry(recentList[selectedIndex])
              if (!descriptor) return
              const fileFromDescriptor = buildGeoTiffFileFromDescriptor(descriptor)
              if (!fileFromDescriptor) {
                Swal.showValidationMessage(fnT('geotiff.popup.recent_invalid'))
                return
              }
              selectedRecentDescriptor = descriptor
              selectedFile = fileFromDescriptor
              console.log('[GeoTIFF][Path] recent selected', {
                sourceType: sourceType,
                descriptorSourcePath: String(descriptor.sourcePath || ''),
                fileSourcePath: String(fileFromDescriptor.sourcePath || ''),
                filePath: String(fileFromDescriptor.path || ''),
                fileName: String(fileFromDescriptor.name || '')
              })
              $selectedFile
                .removeClass('text-body-tertiary')
                .addClass('text-success')
                .text(fnTF('geotiff.selected_file', { name: String(fileFromDescriptor.sourcePath || fileFromDescriptor.path || fileFromDescriptor.name || 'ismeretlen.tif') }))
            })
          }
          const fnRunAutoRecalc = function () {
            if (!selectedFile) return
            if (autoRecalcInFlight) {
              autoRecalcQueued = true
              return
            }
            autoRecalcInFlight = true
            const autoAlignTerrain = $autoAlign.is(':checked')
            fnSetTiffLastFile(selectedFile, sourceType)
            void fnLoadGeoTiffFile(selectedFile, {
              sourceType: sourceType,
              resolution: selectedResolution,
              mode: sourceType,
              autoAlignTerrain: autoAlignTerrain,
              skipLoadingPopup: true
            }).then(function (restored) {
              if (restored) {
                if (selectedRecentDescriptor) {
                  fnPushRecentEntry(sourceType, selectedRecentDescriptor)
                }
                fnQueueRoute3dSyncAfterGeoTiffLoad()
              }
            }).finally(function () {
              autoRecalcInFlight = false
              if (autoRecalcQueued) {
                autoRecalcQueued = false
                fnRunAutoRecalc()
              }
            })
          }
          $range.on('input change', function () {
            selectedResolution = fnSetResolutionInputs($range.val())
          })
          $number.on('input change', function () {
            selectedResolution = fnSetResolutionInputs($number.val())
          })
          $modal.on('click', '[data-tiff-resolution-preset]', function () {
            selectedResolution = fnSetResolutionInputs($(this).data('tiffResolutionPreset'))
          })
          $autoAlign.on('change', function () {
            const autoAlignTerrain = $autoAlign.is(':checked')
            fnPersistAutoAlign(autoAlignTerrain)
            fnRunAutoRecalc()
          })
          const fnSetSelectedFile = function (files) {
            const file = files && files.length ? files[0] : null
            if (!file) return
            selectedRecentDescriptor = null
            if (fnIsElectronRuntime() && !String(file.sourcePath || '').trim()) {
              const runtimePath = String(file.path || file.webkitRelativePath || '').trim()
              if (runtimePath) {
                try {
                  Object.defineProperty(file, 'sourcePath', {
                    value: runtimePath,
                    writable: false,
                    configurable: true,
                    enumerable: true
                  })
                } catch (_error) {
                  file.sourcePath = runtimePath
                }
              }
            }
            selectedFile = file
            const fullPath = String(file.sourcePath || file.path || file.webkitRelativePath || '').trim()
            console.log('[GeoTIFF][Path] file selected', {
              sourceType: sourceType,
              name: String(file.name || ''),
              sourcePath: String(file.sourcePath || ''),
              path: String(file.path || ''),
              webkitRelativePath: String(file.webkitRelativePath || ''),
              resolvedPath: String(file.sourcePath || file.path || file.webkitRelativePath || file.name || ''),
              electronFullPath: fullPath
            })
            $selectedFile
              .removeClass('text-body-tertiary')
              .addClass('text-success')
              .text(fnTF('geotiff.selected_file', { name: String(fullPath || file.name || 'ismeretlen.tif') }))
          }
          const fnSetSelectedElectronPickedFile = function (built) {
            if (!built || !built.file) return
            selectedRecentDescriptor = buildGeoTiffDescriptor(built.file) || null
            selectedFile = built.file
            console.log('[GeoTIFF][Path] electron picker selected', {
              sourceType: sourceType,
              fullPath: built.sourcePath,
              fileName: String(built.file.name || ''),
              size: Number(built.file.size || 0)
            })
            $selectedFile
              .removeClass('text-body-tertiary')
              .addClass('text-success')
              .text(fnTF('geotiff.selected_file', { name: String(built.sourcePath || built.file.name || 'ismeretlen.tif') }))
          }
          const fnOpenElectronPicker = function () {
            void fnSelectFileViaElectronPicker().then(function (built) {
              if (!built || !built.file) return
              fnSetSelectedElectronPickedFile(built)
            }).catch(function (error) {
              console.error('[GeoTIFF][Path] electron picker failed', error)
            })
          }
          if (selectedFile) {
            $selectedFile
              .removeClass('text-body-tertiary')
              .addClass('text-success')
              .text(fnTF('geotiff.prev_loaded', { name: String(selectedFile.path || selectedFile.webkitRelativePath || selectedFile.name || 'ismeretlen.tif') }))
          } else {
            $selectedFile
              .removeClass('text-success')
              .addClass('text-body-tertiary')
              .text(fnT('geotiff.no_selected'))
          }
          $modal.on('dragenter', function (event) {
            event.stopPropagation()
            event.preventDefault()
            $modal.addClass('bg-success-subtle')
          })
          $modal.on('dragover', function (event) {
            event.stopPropagation()
            event.preventDefault()
          })
          $modal.on('drop', function (event) {
            event.stopPropagation()
            event.preventDefault()
            $modal.removeClass('bg-success-subtle')
            if (fnIsElectronRuntime()) {
              void fnSelectFileViaElectronPicker().then(function (built) {
                if (!built || !built.file) return
                fnSetSelectedElectronPickedFile(built)
              }).catch(function (error) {
                console.error('[GeoTIFF][Path] electron picker failed', error)
              })
              return
            }
            fnSetSelectedFile(event.originalEvent.dataTransfer.files)
          })
          $modal.find('[data-import-icon]').on('click', function () {
            if (isElectronRuntime) {
              fnOpenElectronPicker()
              return
            }
            $file.trigger('click')
          })
          $file.on('click', function (event) {
            if (!isElectronRuntime) return
            event.preventDefault()
            event.stopPropagation()
            fnOpenElectronPicker()
          })
          $file.on('keydown', function (event) {
            if (!isElectronRuntime) return
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            event.stopPropagation()
            fnOpenElectronPicker()
          })
          $file.on('input', function () {
            if (isElectronRuntime) {
              // Ignore browser input changes in Electron; native picker is the single source.
              return
            }
            fnSetSelectedFile($file.get(0).files)
          })
        }
      }).then(function (result) {
        if (!result.isConfirmed || !result.value) return
        const payload = result.value
        const currentResolution = fnClampGeoTiffResolution(payload.resolution)
        const importMode = fnNormalizeSourceType(payload.mode)
        const autoAlignTerrain = payload.autoAlignTerrain !== false
        db.settings[resolutionSettingKey] = currentResolution
        db.settings.tiffAutoAlignTerrain = autoAlignTerrain
        if (store && typeof store.setAll === 'function') {
          void store.setAll(db)
        }
        fnSetTiffLastFile(payload.file, sourceType)
        void fnLoadGeoTiffFile(payload.file, {
          sourceType: sourceType,
          resolution: currentResolution,
          mode: importMode,
          autoAlignTerrain: autoAlignTerrain
        }).then(async function (restored) {
          if (restored) {
            const descriptor = await fnEnsureActiveDescriptorForSource(sourceType, payload.descriptor || payload.file)
            if (descriptor) {
              fnPushRecentEntry(sourceType, descriptor)
            }
            fnQueueRoute3dSyncAfterGeoTiffLoad()
          }
        })
      })
    }

    $body.find('[data-leaf-import-tiff-terrain]').on('click', function () {
      fnOpenGeoTiffImportModal('terrain')
    })
    $body.find('[data-leaf-import-tiff-ortho]').on('click', function () {
      fnOpenGeoTiffImportModal('ortho')
    })
    $body.on('click', '[data-tiff-recent-menu-item]', function () {
      const $button = $(this)
      const sourceType = fnNormalizeSourceType($button.data('tiffSourceType'))
      const list = fnGetRecentList(sourceType)
      const idx = parseInt(String($button.data('tiffRecentIndex') || ''), 10)
      if (!Number.isFinite(idx) || idx < 0 || idx >= list.length) return
      const descriptor = fnNormalizeRecentEntry(list[idx])
      if (!descriptor) return
      const fileFromDescriptor = buildGeoTiffFileFromDescriptor(descriptor)
      if (!fileFromDescriptor) return
      const resolutionSettingKey = sourceType === 'terrain' ? 'tiffResolutionTerrain' : 'tiffResolutionOrtho'
      const currentResolution = fnClampGeoTiffResolution((db.settings && db.settings[resolutionSettingKey]) ? db.settings[resolutionSettingKey] : fnGetGeoTiffResolution(fileFromDescriptor.size))
      const autoAlignTerrain = !(db && db.settings && db.settings.tiffAutoAlignTerrain === false)
      fnSetTiffLastFile(fileFromDescriptor, sourceType)
      void fnLoadGeoTiffFile(fileFromDescriptor, {
        sourceType: sourceType,
        resolution: currentResolution,
        mode: sourceType,
        autoAlignTerrain: autoAlignTerrain
      }).then(async function (restored) {
        if (!restored) return
        const activeDescriptor = await fnEnsureActiveDescriptorForSource(sourceType, descriptor || fileFromDescriptor)
        fnPushRecentEntry(sourceType, activeDescriptor || descriptor)
        fnQueueRoute3dSyncAfterGeoTiffLoad()
      }).catch(function (error) {
        const message = String((error && error.message) || error || fnT('geotiff.load_failed'))
        console.error('[GeoTIFF][Recent] load failed', {
          sourceType: sourceType,
          sourcePath: String(fileFromDescriptor.sourcePath || fileFromDescriptor.path || ''),
          error: message
        })
        Swal.fire(message)
      })
    })
    const fnHandleGeoTiffUnload = function (rawSourceType) {
      const sourceType = fnNormalizeSourceType(rawSourceType)
      fnClearGeoTiffLayer(sourceType)
      fnSetTiffLastFile(null, sourceType)
      fnClearLocalTerrainStateAfterUnload(sourceType)
      void fnPersistExplicitTiffUnload(sourceType)
      fnQueueRoute3dSyncAfterGeoTiffLoad()
    }
    $body.on('click', '[data-leaf-unload-tiff-terrain]', function (event) {
      event.preventDefault()
      event.stopPropagation()
      fnHandleGeoTiffUnload('terrain')
    })
    $body.on('click', '[data-leaf-unload-tiff-ortho]', function (event) {
      event.preventDefault()
      event.stopPropagation()
      fnHandleGeoTiffUnload('ortho')
    })
    fnRenderRecentMenuList('terrain')
    fnRenderRecentMenuList('ortho')

    $body.find('[data-leaf-my-location]').on('click', function () {
      if (fnGetMyLocationActive()) {
        fnSetMyLocationButtonActive(false)
        fnClearMyLocationLocateTimer()
        fnSetMyLocationFallbackTried(false)
        const marker = fnGetMyLocationMarker()
        if (marker && map && map.hasLayer(marker)) {
          map.removeLayer(marker)
        }
        fnSetMyLocationMarker(null)
        if (map && typeof map.stopLocate === 'function') map.stopLocate()
        return
      }
      fnSetMyLocationButtonActive(true)
      fnSetMyLocationFallbackTried(false)
      fnClearMyLocationLocateTimer()
      if (map && typeof map.stopLocate === 'function') map.stopLocate()
      fnSetMyLocationLocateTimer(setTimeout(function () {
        fnTryBrowserMyLocationFallback()
      }, 3000))
      map.locate({ setView: false, maxZoom: 15, watch: false, enableHighAccuracy: true, timeout: 10000, maximumAge: 0 })
    })

    $body.find('[data-leaf-setview]').on('click', function () {
      const data = $(this).data('leafSetview')
      map.setView([data.lat, data.lng], data.zoom)
    })

    $body.find('[data-leaf-zoom]').on('click', function () {
      const zoom = $(this).data('leafZoom') * 1
      map.setZoom(map.getZoom() + zoom)
    })

    $body.find('[data-leaf-filter-toggle]').on('click', function () {
      const $button = $(this).addClass('active')
      const toggle = $button.data('leafFilterToggle')
      $('[data-leaf-filter-toggle]').filter(function () {
        const $tog = $(this)
        return $tog.data('leafFilterToggle') === toggle && (!$tog.is($button))
      }).removeClass('active')
    })

    $body.find('[data-leaf-checkbox]').on('click', function () {
      const data = $(this).data()
      const $input = $('.leaflet-right').find('.leaflet-control-layers-selector')
      data.leafCheckbox = data.leafCheckbox ? false : true
      $input.prop('checked', data.leafCheckbox).trigger('click')
    })

    $body.find('[data-leaf-checkbox-labels]').on('click', function () {
      const $btn = $(this)
      const data = $btn.data()
      const $labels = $('[data-leaf-checkbox-label]')
      data.leafCheckboxLabel = data.leafCheckboxLabel ? false : true
      if (data.leafCheckboxLabel) {
        $labels.removeClass('d-none')
        $btn.addClass('active')
      } else {
        $labels.addClass('d-none')
        $btn.removeClass('active')
      }
    })

    $body.find('[data-leaf-filter]').on('click', function () {
      const filter = $(this).data('leafFilter')
      db.settings.filter = _.extend({}, db.settings.filter, filter)
      if (store && typeof store.setAll === 'function') {
        void store.setAll(db)
      }
      fnLayerFilterAll()
    })

    if ($leafletmap && $leafletmap.length) {
      fnGetLayers()
    }

    const fnLeafFilterOnLoad = function () {
      let $leafFilterInDb = null
      $body.find('[data-leaf-filter]').each(function () {
        const $button = $(this)
        const filter = $button.data('leafFilter')
        if (JSON.stringify(db.settings.filter) === JSON.stringify(filter)) {
          $leafFilterInDb = $button
        }
      })
      if ($leafFilterInDb && $leafFilterInDb.length) {
        $leafFilterInDb.trigger('click')
      } else {
        $body.find('[data-leaf-filter]').each(function () {
          const $button = $(this)
          const data = $button.data()
          Object.keys(data).includes('leafFilterDefault') ? $button.trigger('click') : null
        })
      }
    }

    fnLeafFilterOnLoad()

    void fnAutoRestoreGeoTiffFromCache()
  }

export {
  bindCoreUiHandlers
}
